import { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/authContext";
import { checkUserStatus } from "../api/getApi"; // Import the new API function
import Text from "../components/text";

function Login() {
  const [loginStep, setLoginStep] = useState("mobile"); // 'mobile', 'password', 'migrate'
  const [formData, setFormData] = useState({ mobile: "", password: "" });
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // Add state to control the forgot password link visibility
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, login } = useAuth();

  // Effect to redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const pendingActionData = location.state?.pendingAction?.data;
      const redirectPath = location.state?.redirectAfterLogin || "/";

      if (pendingActionData && (redirectPath === "/" || redirectPath === "/filter")) {
        navigate(redirectPath, {
          replace: true,
          state: { formDataForAutoSubmit: pendingActionData, autoSubmit: true },
        });
      } else {
        navigate(redirectPath, { replace: true, state: location.state });
      }
    }
  }, [isAuthenticated, navigate, location.state]);

  // Effect to show messages passed from other pages (e.g., signup prompt)
  useEffect(() => {
    if (location.state?.message) {
      setNotificationMessage(location.state.message);
      // Clear the message from location state to prevent it from reappearing
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    if (generalError) setGeneralError("");
    // Hide the forgot password link when user starts typing again
    if (name === "password") {
      setShowForgotPassword(false);
    }
  };

  // Step 1: Handle Mobile Number Verification
  const handleVerifyMobile = async (e) => {
    e.preventDefault();
    if (!/^[6-9]\d{9}$/.test(formData.mobile)) {
      setErrors({ mobile: "Please enter a valid 10-digit mobile number." });
      return;
    }
    setIsLoading(true);
    setGeneralError("");
    try {
      const res = await checkUserStatus(formData.mobile);
      if (res.success) {
        if (res.status === "password_required") {
          setLoginStep("password");
        } else if (res.status === "migration_required") {
          setLoginStep("migrate");
        } else if (res.status === "not_found") {
          setGeneralError("No account found with this mobile number. Please Sign Up.");
        }
      } else {
        setGeneralError(res.error || "An error occurred.");
      }
    } catch (error) {
      setGeneralError("Could not connect to the server. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Handle Final Login with Password
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!formData.password) {
      setErrors({ password: "Password is required." });
      return;
    }
    setIsLoading(true);
    setGeneralError("");
    setShowForgotPassword(false); // Reset on every login attempt
    try {
      // Pass mobile and password to the login function
      const response = await login(formData.mobile, formData.password);
      if (!response.success) {
        const errorMessage = response.error || "Login failed.";
        setGeneralError(errorMessage);
        // If the error is about invalid credentials, show the forgot password link
        if (errorMessage.toLowerCase().includes("invalid credentials")) {
          setShowForgotPassword(true);
        }
      }
      // On success, the isAuthenticated useEffect will handle redirection
    } catch (err) {
      setGeneralError("An unexpected error occurred. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep = () => {
    if (loginStep === "migrate") {
      return (
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Account Update Required</h2>
          <p className="mt-2 text-sm text-gray-600">
            Welcome back! To enhance security, existing users need to create a new account with a password. We sincerely apologize for this one-time inconvenience.
          </p>
          <p className="mt-3 font-medium text-sm text-gray-700">
            Your saved data will be migrated automatically.
          </p>
          <button
            onClick={() => navigate("/signup", { state: location.state })}
            className="mt-6 w-full flex justify-center items-center bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold py-3 px-4 rounded-lg hover:from-green-600 hover:to-green-700"
          >
            Proceed to Sign Up
          </button>
        </div>
      );
    }

    return (
      <form onSubmit={loginStep === 'mobile' ? handleVerifyMobile : handleLogin} className="space-y-6">
        <div>
          <label htmlFor="mobile" className="block text-sm font-medium text-gray-700">
            Mobile Number
          </label>
          <input
            id="mobile"
            name="mobile"
            type="tel"
            required
            maxLength="10"
            value={formData.mobile}
            onChange={handleChange}
            readOnly={loginStep === 'password'}
            className={`mt-1 w-full px-4 py-2.5 border rounded-lg ${errors.mobile ? "border-red-500" : "border-gray-300"} ${loginStep === 'password' ? 'bg-gray-100' : ''}`}
            placeholder="9876543210"
          />
          {errors.mobile && <p className="mt-1 text-sm text-red-600">{errors.mobile}</p>}
        </div>

        {loginStep === 'password' && (
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              required
              autoFocus
              value={formData.password}
              onChange={handleChange}
              className={`mt-1 w-full px-4 py-2.5 border rounded-lg ${errors.password ? "border-red-500" : "border-gray-300"}`}
              placeholder="Enter your password"
            />
            {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
            {showForgotPassword && (
              <div className="text-right mt-2">
                <Link to="/resetpassword" className="text-sm font-medium text-blue-600 hover:text-blue-500">
                  Forgot Password?
                </Link>
              </div>
            )}
          </div>
        )}

        <div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex justify-center items-center bg-gradient-to-r from-blue-500 to-blue-700 text-white font-semibold py-3 px-4 rounded-lg disabled:opacity-75"
          >
            {isLoading ? "Verifying..." : (loginStep === 'mobile' ? "Continue" : "Login")}
          </button>
        </div>
      </form>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-grow flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-6 py-4 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-800">
                {Text.Login?.Title || "Login to your account"}
              </h2>
            </div>
            <div className="p-6">
              {generalError && (
                <div className="mb-4 bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-md">
                  <p className="text-sm">{generalError}</p>
                </div>
              )}
              {renderStep()}
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600">
                  {Text.Login?.Donthaveanaccount || "Don't have an account?"}{" "}
                  <Link to="/signup" state={location.state} className="font-medium text-blue-600 hover:text-blue-500">
                    {Text.Login?.SignUp || "Create an account"}
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;